import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCaseManagement } from "../../../store/slices/caseManagementSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function CaseManagementPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentCaseManagement, isLoading, error } = useSelector(
    (state) => state.caseManagements
  );

  useEffect(() => {
    if (id) dispatch(fetchCaseManagement(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentCaseManagement) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(t);
        window.onafterprint = null;
      };
    }
  }, [currentCaseManagement, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading Case Management print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentCaseManagement) return null;

  const d = currentCaseManagement;

  return (
    <>
      <style jsx global>{`


        * {
          box-sizing: border-box !important;
          font-family: "Times New Roman", Times, serif !important;
        }

        body {
          background: #fff;
          color: #000;
          font-size: 11pt !important;
          line-height: 1.5 !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .print-container {
          padding: 20px !important;
          width: 100% !important;
          max-width: 800px;
          margin: 0 auto;
        }
        ${printFooterStyles}

        .header-logo {
          display: flex;
          justify-content: center;
          margin-bottom: 10px;
        }

        .header-logo img {
          height: 70px;
        }

        .header-text {
          text-align: center;
          margin-bottom: 30px;
        }

        .header-text h2 {
          font-size: 18pt !important;
          font-weight: bold !important;
          margin: 0 0 4px 0 !important;
          text-transform: uppercase;
        }

        .header-text h3 {
          font-size: 14pt !important;
          font-weight: bold !important;
          margin: 0 !important;
          text-transform: uppercase;
          border-bottom: 2px solid #000;
          display: inline-block;
          padding-bottom: 3px;
        }

        .section-title {
          font-size: 12pt !important;
          font-weight: bold !important;
          background-color: #f2f2f2;
          padding: 6px 10px;
          margin-top: 25px;
          margin-bottom: 15px;
          border-left: 4px solid #000;
          text-transform: uppercase;
        }

        .metadata-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px 24px;
          margin-bottom: 25px;
          border: 1px solid #ccc;
          padding: 15px;
          background-color: #fafafa;
        }

        .meta-item {
          display: flex;
          font-size: 11pt;
        }

        .meta-label {
          font-weight: bold;
          min-width: 150px;
        }

        .meta-value {
          border-bottom: 1px dotted #000;
          flex-grow: 1;
          padding-left: 5px;
        }

        .remarks-box {
          border: 1px solid #ccc;
          padding: 12px;
          min-height: 80px;
          background-color: #fff;
          white-space: pre-wrap;
          font-size: 10.5pt;
        }

        .checkbox-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          margin-bottom: 15px;
        }

        .checkbox-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .chk-box {
          width: 14px;
          height: 14px;
          border: 1px solid #000;
          display: inline-block;
          text-align: center;
          line-height: 12px;
          font-weight: bold;
          font-size: 10px;
        }

        .footer-signatures {
          margin-top: 60px;
          display: flex;
          justify-content: space-between;
        }

        .signature-line {
          width: 200px;
          border-top: 1px solid #000;
          text-align: center;
          padding-top: 5px;
          font-size: 10pt;
        }
      `}</style>

      <PrintWrapper>
      <div className="print-container">
        {/* LOGO */}
        <div className="header-logo">
          <img src={logo} alt="The Recovery House Logo" />
        </div>

        {/* HEADER SECTION */}
        <div className="header-text">
          <h2>THE RECOVERY HOUSE (TRH)</h2>
          <h3>Case Management Interaction Report</h3>
        </div>

        {/* SECTION 1: Client & Interaction Details */}
        <div className="section-title">Client & Interaction Details</div>
        <div className="metadata-grid">
          <div className="meta-item" style={{ gridColumn: 'span 2' }}>
            <span className="meta-label">Client Name:</span>
            <span className="meta-value">
              {d.patient ? `${d.patient.first_name || ""} ${d.patient.last_name || ""}`.trim() : "-"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">MR Number:</span>
            <span className="meta-value">{d.patient?.mr_no || "-"}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Case Manager:</span>
            <span className="meta-value">{d.case_manager || "-"}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Call Type:</span>
            <span className="meta-value" style={{ textTransform: 'capitalize' }}>{d.call_type || "-"}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Interaction Type:</span>
            <span className="meta-value">{d.interaction_type || "-"}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Calling Date:</span>
            <span className="meta-value">
              {d.calling_date ? new Date(d.calling_date).toLocaleDateString("en-GB") : "-"}
            </span>
          </div>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <span className="fw-bold d-block mb-1">Remarks / Notes:</span>
          <div className="remarks-box">{d.remarks || "No remarks provided."}</div>
        </div>

        {/* SECTION 2: Employment / Occupational Status */}
        <div className="section-title">Employment / Occupational Status</div>
        <div className="checkbox-grid">
          {[
            "Working",
            "Studying",
            "Looking for a Job",
            "Never Employed",
            "Inducted in Family Business",
            "Managing Household"
          ].map((opt) => {
            const checked = (d.occupational_status || []).includes(opt);
            return (
              <div className="checkbox-item" key={opt}>
                <span className="chk-box">{checked ? "✓" : ""}</span>
                <span>{opt}</span>
              </div>
            );
          })}
          <div className="checkbox-item" style={{ gridColumn: 'span 2' }}>
            <span className="chk-box">{(d.occupational_status || []).includes("others") ? "✓" : ""}</span>
            <span>Others: {d.occupational_status_other || <span style={{ color: '#aaa', fontStyle: 'italic' }}>None specified</span>}</span>
          </div>
        </div>

        {/* SECTION 3: Review Schedule */}
        <div className="section-title">Review Schedule</div>
        <div style={{ marginBottom: "20px" }}>
          <span className="fw-bold d-block mb-1">Review Frequency:</span>
          <div className="remarks-box" style={{ minHeight: '60px' }}>{d.review_frequency || "No frequency specified."}</div>
        </div>

        {/* SECTION 4: Service Schedules */}
        <div className="section-title">Service Schedules</div>
        <div className="metadata-grid">
          <div className="meta-item">
            <span className="meta-label">Service Selected:</span>
            <span className="meta-value">{d.service_type || "-"}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Schedule Date:</span>
            <span className="meta-value">
              {d.service_schedule_date ? new Date(d.service_schedule_date).toLocaleDateString("en-GB") : "-"}
            </span>
          </div>
          <div className="meta-item" style={{ gridColumn: 'span 2' }}>
            <span className="meta-label">Frequency:</span>
            <span className="meta-value">{d.service_frequency || "-"}</span>
          </div>
        </div>

        {/* SIGNATURES */}
        <div className="footer-signatures">
          <div className="signature-line">Case Manager Signature</div>
          <div className="signature-line">Supervisor Signature</div>
        </div>
      </div>
      </PrintWrapper>
    </>
  );
}
